<template>
  <canvas
    ref="canvas"
    class="webgl-canvas"
    :style="{ 'background-color': this.backgroundColor }"
  />
</template>

<script>
import * as THREE from 'three';
import { markRaw } from 'vue';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { components } from 'locales';
import * as PcbStackup from 'helper/pcb_stackup';
import * as PcbMaterials from 'helper/pcb_materials';
import { STLLoader } from 'three/addons/loaders/STLLoader';
import JSZip from 'jszip';
import _ from 'lodash';

let scene = null;
let renderer = null;
let camera = null;
let controls = null;
let lightHolder = null;
let boardHolder = null;
let partsHolder = null;
let partsInteractionHolder = null;
let pin1Holder = null;
let noteHolder = null;
let boundingBox = null;
let raycaster = null;
const pointer = new THREE.Vector2();
const interactionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

export default {
  props: {
    stlAssetsPath: String,
    mode: String,
    stackupViewPreset: String,
    stackupPreset: String,
    materialPreset: String,
    offsetX: Number,
    offsetY: Number,
    backgroundColor: String,
    enableMeasurement: Boolean,
  },
  data() {
    return {
      processingFailed: false,
      bus: this.$root.bus,
      meshes: null,
      stackup: PcbStackup[this.stackupPreset],
      materials: PcbMaterials[this.materialPreset],
      initialized: false,
      width: 0,
      height: 0,
      cameraZ: 1,
      measurementPt: null,
    };
  },
  computed: {},
  mounted() {
    this.width = this.$refs.canvas.clientWidth;
    this.height = this.$refs.canvas.clientHeight;

    this.$root.message = this.$t('loading');
    this.init();
    this.initControls();
    this.initBus();
    this.initEvents();
    this.loadAssets().then((m) => {
      this.meshes = m;
      this.$root.message = this.$t('loading3D');
      this.loadModel();
      this.updateStackup();
      this.initRenderings();
      this.bus.emit('publishViewport', { viewport: { width: this.width, height: this.height }, boundingBox });

      this.$root.message = this.$t('ready');
      this.initialized = true;
      this.bus.emit('viewerReady');

      this.animate();
    });
  },
  methods: {
    init() {
      const self = this;
      const canvas = self.$refs.canvas;

      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'default' });
      renderer.setClearColor('#2E3440', 1.0);
      renderer.setSize(self.width, self.height);
      renderer.setPixelRatio(window.devicePixelRatio);

      scene = new THREE.Scene();
      scene.background = new THREE.Color(this.backgroundColor);

      lightHolder = new THREE.Group();
      const light = new THREE.AmbientLight(0xffffff, Math.PI);
      lightHolder.add(light);
      scene.add(lightHolder);

      if (self.mode === 'assembly') {
        camera = new THREE.PerspectiveCamera(40, self.width / self.height, 1, 1000);
      } else if (self.mode === 'layout') {
        camera = new THREE.OrthographicCamera(
          self.width / -2,
          self.width / 2,
          self.height / 2,
          self.height / -2,
          -1000,
          1000,
        );
        camera.position.set(0, 0, 1);
        camera.lookAt(scene.position);
      }
      scene.add(camera);
      raycaster = new THREE.Raycaster();
    },
    initRenderings() {
      const to = { x: 0.0, y: 0.0, z: -camera.position.z };
      camera.position.set(to.x, to.y, to.z);
      camera.lookAt(scene.position);
      lightHolder.quaternion.copy(camera.quaternion);
      renderer.render(scene, camera);
      this.bus.emit('publishRendering', { data: renderer.domElement.toDataURL(), side: 'back' });
      to.z = -to.z;
      camera.position.set(to.x, to.y, to.z);
      camera.lookAt(scene.position);
      lightHolder.quaternion.copy(camera.quaternion);
      renderer.render(scene, camera);
      this.bus.emit('publishRendering', { data: renderer.domElement.toDataURL(), side: 'front' });
    },
    initEvents() {
      renderer.domElement.addEventListener('pointerdown', this.checkPartSelection);
      renderer.domElement.addEventListener('mousemove', this.mousePositionHandler);
      window.addEventListener('keydown', this.handleKeyEvents);
    },
    loadModel() {
      const board = this.meshes.find((mesh) => mesh.name === 'board');
      board.geometry.computeBoundingBox();
      boundingBox = board.geometry.boundingBox;

      boardHolder = new THREE.Group();
      scene.add(boardHolder);
      partsHolder = new THREE.Group();
      scene.add(partsHolder);
      pin1Holder = new THREE.Group();
      scene.add(pin1Holder);
      partsInteractionHolder = new THREE.Group();
      scene.add(partsInteractionHolder);
      noteHolder = new THREE.Group();
      scene.add(noteHolder);

      if (camera.type === 'PerspectiveCamera') {
        this.fitCameraToSelection(board, 1.1);
      } else if (camera.type === 'OrthographicCamera') {
        this.fitOrthoPerspective(1.1);
      }
    },
    async loadAssets() {
      const loader = new STLLoader();
      const request = new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.onprogress = (evt) => {
          if (evt.lengthComputable) {
            const percentComplete = Math.round((evt.loaded / evt.total) * 100).toString();
            this.$root.message = `${this.$t('loading')}... ${percentComplete.padStart(3, ' ')} %`;
          } else {
            const bytesComplete = evt.loaded.toString();
            this.$root.message = `${this.$t('loading')}... ${bytesComplete.padStart(10, ' ')} bytes`;
          }
        };
        xhr.onload = () => {
          resolve(xhr);
        };
        xhr.responseType = 'arraybuffer';
        xhr.open('GET', this.stlAssetsPath);
        xhr.send();
      });
      const rsp = await request;

      let meshes = null;
      const zip = new JSZip();
      await zip.loadAsync(rsp.response).then((content) => {
        this.$root.message = this.$t('extracting');
        meshes = _.map(content.files, (file) => new Promise((resolve) => {
          file.async('ArrayBuffer').then((buffer) => {
            const name = file.name.split('.')[0];
            this.$root.message = `${this.$t('extracting')} ${name.padStart(20, ' ')}...`;
            let mesh = null;
            if (name === 'boardoutline') {
              mesh = markRaw(new THREE.LineSegments(new THREE.EdgesGeometry((loader.parse(buffer)))));
            } else {
              mesh = markRaw(new THREE.Mesh(loader.parse(buffer)));
            }
            mesh.name = name;
            resolve(mesh);
          });
        }));
      });
      const resolvedMeshes = await Promise.allSettled(meshes);
      return resolvedMeshes.filter((promise) => promise.value !== null)
        .map((promise) => promise.value);
    },
    animate() {
      lightHolder.quaternion.copy(camera.quaternion);
      const viewport = [
        new THREE.Vector2(-1, -1),
        new THREE.Vector2(1, -1),
        new THREE.Vector2(1, 1),
        new THREE.Vector2(-1, 1),
      ].map((pt) => {
        raycaster.setFromCamera(pt, camera);
        const intersects = new THREE.Vector3();
        raycaster.ray.intersectPlane(interactionPlane, intersects);
        return intersects;
      });
      this.bus.emit('publishFocusViewport', viewport);

      if (this.cameraZ > 0 && camera.position.z < 0) {
        this.bus.emit('sideChanged', 'back');
      } else if (this.cameraZ < 0 && camera.position.z > 0) {
        this.bus.emit('sideChanged', 'front');
      }
      this.cameraZ = camera.position.z;

      renderer.render(scene, camera);
    },
    updateStackup() {
      const self = this;
      let mesh = null;
      boardHolder.clear();
      self.stackup.forEach((layer) => {
        const material = self.materials[layer.name];
        if (material === undefined) {
          // eslint-disable-next-line no-console
          console.warn(`Material for ${layer.name} not found, skipping layer...`);
        } else {
          mesh = self.meshes.find((m) => m.name === layer.name);
          if (mesh === undefined) {
            // eslint-disable-next-line no-console
            console.warn(`Mesh for ${layer.name} not found, skipping layer...`);
          } else {
            mesh = mesh.clone();
            mesh.name = layer.name;
            mesh.enabled = true;
            mesh.material = material;
            mesh.position.z = layer.offset;
            mesh.renderOrder = layer.order;
            boardHolder.add(mesh);
          }
        }
      });
    },
    initBus() {
      this.bus.on('addNote', (note) => {
        note.shapes.forEach((shape) => {
          if (shape.length <= 1) {
            const cone = new THREE.Mesh(
              new THREE.ConeGeometry(1, 4, 8),
              this.materials.note,
            );
            cone.name = note.id;
            if (camera.type === 'OrthographicCamera') {
              cone.position.x = shape[0].x + 1.5;
              cone.position.y = shape[0].y + 1.5;
              cone.position.z = 20;
              cone.rotation.z = THREE.MathUtils.degToRad(135.0);
            } else {
              cone.position.x = shape[0].x;
              cone.position.y = shape[0].y;
              cone.position.z = 2;
              cone.rotation.x = -THREE.MathUtils.degToRad(90.0);
            }
            noteHolder.add(cone);
          } else {
            const path = new THREE.Shape(shape.map((coord) => new THREE.Vector2(coord.x, coord.y)));
            const extrudeSettings = { depth: 1.6 };
            const geometry = new THREE.ExtrudeGeometry(path, extrudeSettings);
            const mesh = new THREE.Mesh(geometry, this.materials.note);
            mesh.name = note.id;
            mesh.position.z = -0.8;
            noteHolder.add(mesh);
          }
        });
        noteHolder.position.x = -this.offsetX + boundingBox.min.x;
        noteHolder.position.y = -this.offsetY + boundingBox.min.y;
        this.animate();
      });
      this.bus.on('removeNote', (note) => {
        noteHolder.remove(noteHolder.getObjectByName(note.id));
        this.animate();
      });
      this.bus.on('modeToggle', (mode) => {
        if (mode === 'layers') {
          this.stackup = PcbStackup[this.stackupViewPreset];
          this.materials = PcbMaterials.layout;
        } else if (mode === 'preview') {
          this.stackup = PcbStackup[this.stackupPreset];
          this.materials = PcbMaterials[this.materialPreset];
        } else if (mode === 'stencil') {
          this.stackup = PcbStackup.layoutRealStencil;
          this.materials = PcbMaterials[this.materialPreset];
        }
        this.updateStackup();
        this.animate();
      });
      this.bus.on('changePerspective', (perspective) => {
        const zInvert = camera.position.z < 0 ? 1 : -1;
        controls.reset();

        let to;
        if (perspective === 'front') {
          to = { x: 0.0, y: 0.0, z: camera.position.z };
        } else if (perspective === 'back') {
          to = { x: 0.0, y: 0.0, z: -camera.position.z };
        } else if (perspective === 'turn') {
          to = { x: 0.0, y: 0.0, z: camera.position.z * zInvert };
        }
        camera.position.set(to.x, to.y, to.z);
        camera.lookAt(scene.position);
        controls.update();
        this.animate();
      });
      this.bus.on('loadParts', (parts) => {
        if (!this.initialized) {
          return;
        }
        const margin = 0.25;
        parts.forEach((part) => {
          const zSpread = part.width * part.height * 0.0001;
          const dummy = new THREE.Mesh(new THREE.BoxGeometry(
            part.width - margin,
            part.height - margin,
            0.8 - zSpread,
          ));
          dummy.name = part.id;
          dummy.material = this.materials.partNotPlaced;
          dummy.position.x = part.center.x;
          dummy.position.y = part.center.y;
          dummy.position.z = part.side === 'top' ? 1.2 : -1.2;
          dummy.rotation.z = THREE.MathUtils.degToRad(part.rotation);
          partsHolder.add(dummy);

          const box = new THREE.Mesh(new THREE.BoxGeometry(part.width, part.height, 0.9 - zSpread));
          box.name = part.id;
          box.material = this.materials.partSelected;
          box.position.x = part.center.x;
          box.position.y = part.center.y;
          box.position.z = part.side === 'top' ? 1.2 : -1.2;
          box.rotation.z = THREE.MathUtils.degToRad(part.rotation);
          box.visible = false;
          partsInteractionHolder.add(box);

          const pin1 = new THREE.Mesh(
            new THREE.ConeGeometry(0.4, 1, 10),
            this.materials.pin1,
          );
          if (part.pin1.x !== 0) {
            pin1.name = part.id;
            pin1.position.x = part.pin1.x;
            pin1.position.y = part.pin1.y;
            if (part.side === 'top') {
              pin1.position.z = 2.0;
              pin1.rotation.x = -THREE.MathUtils.degToRad(90.0);
            } else if (part.side === 'bottom') {
              pin1.position.z = -2.0;
              pin1.rotation.x = THREE.MathUtils.degToRad(90.0);
            }
            pin1Holder.add(pin1);
          }
        });
        partsHolder.position.x = -this.offsetX + boundingBox.min.x;
        partsHolder.position.y = -this.offsetY + boundingBox.min.y;
        pin1Holder.position.x = -this.offsetX + boundingBox.min.x;
        pin1Holder.position.y = -this.offsetY + boundingBox.min.y;
        partsInteractionHolder.position.x = -this.offsetX + boundingBox.min.x;
        partsInteractionHolder.position.y = -this.offsetY + boundingBox.min.y;
        this.animate();
      });
      this.bus.on('updatePartFocus', ({ parts, updateCamera }) => {
        const list = parts.map((part) => part.id);
        const box = new THREE.Box3();
        partsInteractionHolder.children.forEach((child) => {
          if (list.includes(child.name)) {
            child.visible = true;
            box.expandByObject(child);
          } else {
            child.visible = false;
          }
        });
        const w2 = (box.max.x - box.min.x) / 2;
        const h2 = (box.max.y - box.min.y) / 2;
        if (updateCamera) {
          controls.target.x = box.min.x + w2;
          controls.target.y = box.min.y + h2;
          controls.object.position.x = box.min.x + w2;
          controls.object.position.y = box.min.y + h2;
          if (box.max.z <= 0.0) {
            controls.object.position.z = -60.0;
          } else {
            controls.object.position.z = 60.0;
          }
        }
        controls.update();
        this.animate();
      });
      this.bus.on('updatePartPlacement', (parts) => {
        if (partsHolder === null) {
          return;
        }
        const list = parts.map((part) => part.id);
        partsHolder.children.forEach((child) => {
          if (list.includes(child.name)) {
            child.material = this.materials.partPlaced;
          } else {
            child.material = this.materials.partNotPlaced;
          }
        });
        controls.update();
        this.animate();
      });
      this.bus.on('updateShape', ({ shape, value }) => {
        boardHolder.children.find((child) => child.name === shape).visible = value;
        this.animate();
      });
    },
    initControls() {
      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableRotate = this.mode !== 'layout';
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.ROTATE,
      };
      controls.minDistance = 10;
      controls.maxDistance = 400;
      controls.zoomSpeed = 3.0;
      controls.addEventListener('change', this.animate); // use if there is no animation loop

      controls.update();
    },
    fitCameraToSelection(selection, scale) {
      const box = new THREE.Box3();
      box.setFromObject(selection);
      const size = new THREE.Vector3();
      box.getSize(size);

      const fov = camera.fov * (Math.PI / 180);
      const fovh = 2 * Math.atan(Math.tan(fov / 2) * camera.aspect);
      const dx = size.z / 2 + Math.abs(size.x / 2 / Math.tan(fovh / 2));
      const dy = size.z / 2 + Math.abs(size.y / 2 / Math.tan(fov / 2));
      let cameraZ = Math.max(dx, dy);

      if (scale !== undefined && scale !== 0) cameraZ *= scale;

      camera.position.set(0, 0, cameraZ);

      // set the far plane of the camera so that it easily encompasses the whole object
      const minZ = boundingBox.min.z;
      const cameraToFarEdge = (minZ < 0) ? -minZ + cameraZ : cameraZ - minZ;

      camera.far = cameraToFarEdge * 3;
      camera.updateProjectionMatrix();

      controls.target = new THREE.Vector3(0, 0, 0);
      controls.maxDistance = cameraToFarEdge * 2;

      controls.saveState();
    },
    checkPartSelection() {
      raycaster.setFromCamera(pointer, camera);

      const collision = new THREE.Vector3();
      raycaster.ray.intersectPlane(interactionPlane, collision);

      const intersects = raycaster.intersectObjects(partsHolder.children);

      if (intersects.length <= 0) {
        return;
      }
      if (intersects[0].distance > camera.position.distanceTo(collision)) {
        return;
      }

      this.animate();

      this.bus.emit('partClicked', intersects[0].object.name);
    },
    fitOrthoPerspective(scale) {
      camera.zoom = Math.min(
        this.width / (boundingBox.max.x - boundingBox.min.x),
        this.height / (boundingBox.max.y - boundingBox.min.y),
      ) / scale;
      camera.updateProjectionMatrix();
      controls.saveState();
    },
    handleKeyEvents(evt) {
      if (!this.enableMeasurement) {
        return;
      }

      switch (evt.key) {
        case ' ':
          evt.preventDefault();
          if (this.measurementPt) {
            this.measurementPt = null;
            this.$root.message = '';
          } else {
            this.measurementPt = pointer.clone();
            this.$root.message = `${this.$t('distance')}   0.00mm`;
          }
          break;
        case 'Escape':
          evt.preventDefault();
          this.measurementPt = null;
          this.$root.message = '';
          break;
        default:
      }
    },
    mousePositionHandler(evt) {
      const bb = renderer.domElement.getBoundingClientRect();
      pointer.x = ((evt.pageX - bb.left) / this.width) * 2 - 1;
      pointer.y = -((evt.pageY - bb.top) / this.height) * 2 + 1;

      if (this.measurementPt) {
        const pt1 = new THREE.Vector3();
        const pt2 = new THREE.Vector3();
        raycaster.setFromCamera(this.measurementPt, camera);
        raycaster.ray.intersectPlane(interactionPlane, pt1);
        raycaster.setFromCamera(pointer, camera);
        raycaster.ray.intersectPlane(interactionPlane, pt2);
        this.$root.message = `${this.$t('distance')} ${pt1.distanceTo(pt2).toFixed(2).toString().padStart(6, ' ')}mm`;
      }
    },
  },
  name: 'WebGlViewer',
  i18n: components.WebGlViewer,
};
</script>

<style scoped lang="scss">
</style>
